import { HttpClient, HttpHeaders, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Student } from 'src/app/models/student';
import { environment } from 'src/environments/environment';

/** Result of sending the app access mail (QR code), see backend libs/access/access.php. */
export type AccessMailResult = 'sent' | 'disabled' | 'not-found' | 'no-email' | 'failed' | 'not-imported';

export function accessMailMessage(result: AccessMailResult, email: string): string {
  switch (result) {
    case 'sent': return `Der App-Zugang (QR-Code) wurde an ${email} gesendet.`;
    case 'no-email': return 'Keine gültige E-Mail-Adresse hinterlegt, der App-Zugang wurde nicht gesendet.';
    case 'disabled': return 'Der Versand von App-Zugängen ist derzeit deaktiviert.';
    case 'not-found': return 'Der Student wurde nicht gefunden.';
    default: return 'Die E-Mail mit dem App-Zugang konnte nicht gesendet werden.';
  }
}

/**
 * Angular's default codec leaves "+", "@", ":" etc. unencoded in values;
 * PHP would decode "+" as a space.
 */
class StrictEncoder extends HttpUrlEncodingCodec {
  override encodeKey(key: string): string { return encodeURIComponent(key); }
  override encodeValue(value: string): string { return encodeURIComponent(value); }
}

@Injectable({
  providedIn: 'root',
})
export class StudentsService {
  private readonly serviceName = 'students';
  private readonly headers = { headers: new HttpHeaders({ "Authorization": `Bearer ${environment.apiKeyInternal}`})
  }; 

  constructor(private http: HttpClient) {}

  public get(): Observable<Student[]> {
    return this.http.get<Student[]>(
      `${environment.apiUrl}${this.serviceName}/get.php`, this.headers
    );
  }

  public set(student: Student): Observable<{result: boolean; sid: number}> {
    // URL-encoded, so that e.g. "+" in an email or "&" in a name arrive unchanged.
    const payload = new HttpParams({
      fromObject: {
        sid: String(student.sid ?? ''),
        firstname: student.firstname ?? '',
        lastname: student.lastname ?? '',
        birthday: String(student.birthday ?? ''),
        gender: String(student.gender ?? ''),
        street: student.street ?? '',
        house: String(student.house ?? ''),
        house_suffix: student.house_suffix ?? '',
        zip: student.zip ?? '',
        city: student.city ?? '',
        phone: student.phone ?? '',
        email: student.email ?? ''
      },
      encoder: new StrictEncoder()
    }).toString();

    return this.http.post<{ result: boolean; sid: number }>(
      `${environment.apiUrl}${this.serviceName}/set.php`,
      payload,
      this.headers
    );
  }

  public del(student: Student): Observable<void> {
    const payload = `sid=${student.sid}`;

    return this.http.post<void>(
      `${environment.apiUrl}${this.serviceName}/del.php`,
      payload,
      this.headers
    );
  }

  /** Sends a new app access mail (QR code); older unused QR codes of the student become invalid. */
  public sendAccess(student: Student): Observable<{ result: AccessMailResult }> {
    const payload = `sid=${student.sid}`;

    return this.http.post<{ result: AccessMailResult }>(
      `${environment.apiUrl}${this.serviceName}/access.php`,
      payload,
      this.headers
    );
  }

  public export(): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}${this.serviceName}/export.php`, { responseType: 'arraybuffer', ... this.headers }
    );
  }
}
